import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import {
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  PlayCircle,
  PackageCheck,
  Truck,
  Clock
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const MaterialRequests = () => {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    request_type: 'repuestos',
    item_name: '',
    quantity: 1,
    unit: 'unidad',
    justification: '',
    notes: ''
  });

  const [cart, setCart] = useState([]);
  const [priority, setPriority] = useState('normal');
  const [generalJustification, setGeneralJustification] = useState('');
  const [generalNotes, setGeneralNotes] = useState('');
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptRequest, setReceiptRequest] = useState(null);
  const [receiptItems, setReceiptItems] = useState([]);
  const [receiptNotes, setReceiptNotes] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const filteredRequests = requests.filter((request) => { const term = searchTerm.trim().toLowerCase(); if (term === "") return true; const items = Array.isArray(request.items) ? request.items : [{ item_name: request.item_name || "", request_type: request.request_type || "" }]; return String(request.order_number || "").toLowerCase().includes(term) || String(request.requester_name || "").toLowerCase().includes(term) || items.some(item => String(item.item_name || "").toLowerCase().includes(term) || String(item.request_type || "").toLowerCase().includes(term)); });

  const fetchRequests = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/material-requests${params}`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const addItemToCart = () => {
    if (!formData.item_name.trim()) {
      toast.error('Debe indicar el artículo o material');
      return;
    }

    if (!formData.quantity || Number(formData.quantity) <= 0) {
      toast.error('La cantidad debe ser mayor que cero');
      return;
    }

    if (!formData.justification.trim()) {
      toast.error('Debe indicar la justificación del artículo');
      return;
    }

    setCart((prev) => [
      ...prev,
      {
        request_type: formData.request_type,
        item_name: formData.item_name.trim(),
        quantity: Number(formData.quantity),
        unit: formData.unit,
        justification: formData.justification.trim(),
        notes: formData.notes.trim()
      }
    ]);

    setFormData({
      request_type: 'repuestos',
      item_name: '',
      quantity: 1,
      unit: 'unidad',
      justification: '',
      notes: ''
    });

    toast.success('Artículo agregado al pedido');
  };

  const removeItemFromCart = (index) => {
    setCart((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      toast.error('Debe agregar al menos un artículo al pedido');
      return;
    }

    if (!generalJustification.trim()) {
      toast.error('Debe indicar la justificación general del pedido');
      return;
    }

    try {
      await axios.post(`${API}/material-requests`, {
        priority,
        justification: generalJustification.trim(),
        notes: generalNotes.trim() || null,
        items: cart
      });

      toast.success('Pedido creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(`${API}/material-requests/${id}/approve`);
      toast.success('Pedido aprobado');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('¿Estás seguro de rechazar este pedido?')) return;
    try {
      await axios.patch(`${API}/material-requests/${id}/reject`);
      toast.success('Pedido rechazado');
      fetchRequests();
    } catch (error) {
      toast.error('Error al rechazar');
    }
  };

  const handlePrepare = async (id) => {
    try {
      await axios.patch(`${API}/material-requests/${id}/prepare`);
      toast.success('Pedido puesto en preparación');
      fetchRequests();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error al iniciar la preparación'
      );
    }
  };

  const handleReady = async (id) => {
    try {
      await axios.patch(`${API}/material-requests/${id}/ready`);
      toast.success('Pedido marcado como listo para entregar');
      fetchRequests();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error al marcar el pedido como listo'
      );
    }
  };

  const handleDeliver = async (id) => {
    if (!window.confirm('¿Confirmar que este pedido fue entregado?')) return;

    try {
      await axios.patch(`${API}/material-requests/${id}/deliver`, {
        delivery_notes: null
      });
      toast.success('Pedido entregado correctamente');
      fetchRequests();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error al entregar el pedido'
      );
    }
  };

  const openDetailDialog = async (request) => {
    setDetailRequest(request);
    setDetailHistory([]);
    setDetailDialogOpen(true);

    try {
          const response = await axios.get(`${API}/material-requests/${request.id}/history`);
      setDetailHistory(response.data.history || []);
    } catch (error) {
      console.error('Error cargando historial del pedido:', error);
      toast.error('No se pudo cargar el historial de trazabilidad');
    }
  };

  const exportMaterialRequestPDF = (request) => {
    if (!request) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 16;
    const contentWidth = pageWidth - margin * 2;

    const statusLabels = {
      pending: 'Pendiente',
      approved: 'Aprobado',
      preparing: 'En preparación',
      ready: 'Listo para recibir',
      partial: 'Recepción parcial',
      delivered: 'Recepción completada',
      rejected: 'Rechazado'
    };

    const priorityLabels = {
      low: 'Baja',
      normal: 'Normal',
      high: 'Alta',
      urgent: 'Urgente'
    };

    const actionLabels = {
      create: 'Solicitud creada',
      approve: 'Pedido aprobado',
      reject: 'Pedido rechazado',
      prepare: 'Pedido preparado',
      ready: 'Pedido listo para recibir',
      deliver: 'Pedido entregado',
      partial_delivery: 'Entrega parcial registrada',
      confirm_receipt: 'Recepción confirmada'
    };

    const statusLabel = (value) =>
      statusLabels[value] || value || '-';

    const priorityLabel = (value) =>
      priorityLabels[value] || value || '-';

    const actionLabel = (value) =>
      actionLabels[value] || 'Evento registrado';

    const safeText = (value) => {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      return String(value);
    };

    const formatDate = (value) => {
      if (!value) return '-';

      try {
        return new Date(value).toLocaleString('es-BO', {
          dateStyle: 'short',
          timeStyle: 'short'
        });
      } catch {
        return value;
      }
    };

    const items = Array.isArray(request.items)
      ? request.items
      : [];

    const totalRequested = items.reduce(
      (sum, item) =>
        sum +
        Number(
          item.quantity_requested ??
          item.quantity ??
          0
        ),
      0
    );

    const totalReceived = items.reduce(
      (sum, item) =>
        sum +
        Number(item.quantity_delivered ?? 0),
      0
    );

    const totalPending = items.reduce(
      (sum, item) =>
        sum +
        Number(
          item.quantity_pending ??
          Math.max(
            Number(
              item.quantity_requested ??
              item.quantity ??
              0
            ) -
              Number(item.quantity_delivered ?? 0),
            0
          )
        ),
      0
    );

    const addPageHeader = () => {
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 20, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('MAQControl', margin, 12);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(
        'Pedido de Material',
        pageWidth - margin,
        12,
        { align: 'right' }
      );
    };

    const addFooter = () => {
      const pageCount = doc.getNumberOfPages();

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        doc.setDrawColor(226, 232, 240);
        doc.line(
          margin,
          pageHeight - 14,
          pageWidth - margin,
          pageHeight - 14
        );

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);

        doc.text(
          `MAQControl · ${safeText(
            request.order_number || request.id
          )}`,
          margin,
          pageHeight - 8
        );

        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth - margin,
          pageHeight - 8,
          { align: 'right' }
        );
      }
    };

    const ensureSpace = (y, needed = 20) => {
      if (y + needed > pageHeight - 22) {
        doc.addPage();
        addPageHeader();
        return 30;
      }

      return y;
    };

    const sectionTitle = (title, y) => {
      y = ensureSpace(y, 16);

      doc.setFillColor(30, 41, 59);
      doc.roundedRect(
        margin,
        y,
        contentWidth,
        8,
        2,
        2,
        'F'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text(title, margin + 4, y + 5.5);

      return y + 13;
    };

    // =====================================================
    // ENCABEZADO PRINCIPAL
    // =====================================================

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 38, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text('MAQControl', margin, 15);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      'Sistema de Gestión de Maquinaria y Operaciones',
      margin,
      23
    );

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(
      'PEDIDO DE MATERIAL',
      pageWidth - margin,
      14,
      { align: 'right' }
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      'Documento de control interno',
      pageWidth - margin,
      22,
      { align: 'right' }
    );

    let y = 49;

    // =====================================================
    // IDENTIFICACIÓN
    // =====================================================

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(
      safeText(
        request.order_number || 'PEDIDO SIN NÚMERO'
      ),
      margin,
      y
    );

    const statusText = statusLabel(
      request.status
    ).toUpperCase();

    const statusWidth = 45;

    doc.setFillColor(241, 245, 249);
    doc.roundedRect(
      pageWidth - margin - statusWidth,
      y - 8,
      statusWidth,
      12,
      3,
      3,
      'F'
    );

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(51, 65, 85);
    doc.text(
      statusText,
      pageWidth - margin - statusWidth / 2,
      y,
      { align: 'center' }
    );

    y += 11;

    doc.setDrawColor(226, 232, 240);
    doc.line(
      margin,
      y,
      pageWidth - margin,
      y
    );

    y += 9;

    // =====================================================
    // DATOS DEL PEDIDO
    // =====================================================

    const info = [
      [
        'SOLICITADO POR',
        safeText(request.requester_name)
      ],
      [
        'FECHA DE SOLICITUD',
        formatDate(request.created_at)
      ],
      [
        'PRIORIDAD',
        priorityLabel(request.priority)
      ],
      [
        'ID INTERNO',
        safeText(request.id)
      ]
    ];

    const infoColWidth =
      contentWidth / 2;

    info.forEach((entry, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);

      const x =
        margin +
        col * infoColWidth;

      const iy =
        y +
        row * 14;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(entry[0], x, iy);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      doc.text(entry[1], x, iy + 5);
    });

    y += 30;

    // =====================================================
    // MATERIALES
    // =====================================================

    y = sectionTitle(
      'DETALLE DE MATERIALES',
      y
    );

    const tableX = margin;
    const tableWidth = contentWidth;

    const columns = [
      { title: 'ARTÍCULO', width: 50 },
      { title: 'TIPO', width: 28 },
      { title: 'SOLIC.', width: 20 },
      { title: 'RECIB.', width: 20 },
      { title: 'PEND.', width: 20 },
      { title: 'ESTADO', width: 28 }
    ];

    const drawTableHeader = () => {
      doc.setFillColor(226, 232, 240);
      doc.rect(
        tableX,
        y,
        tableWidth,
        9,
        'F'
      );

      let headerX = tableX;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(51, 65, 85);

      columns.forEach((column) => {
        doc.text(
          column.title,
          headerX + 2,
          y + 6
        );

        headerX += column.width;
      });

      y += 9;
    };

    drawTableHeader();

    items.forEach((item) => {
      const requested = Number(
        item.quantity_requested ??
        item.quantity ??
        0
      );

      const received = Number(
        item.quantity_delivered ?? 0
      );

      const pending = Number(
        item.quantity_pending ??
        Math.max(
          requested - received,
          0
        )
      );

      const itemStatus =
        item.delivery_status === 'complete'
          ? 'Completo'
          : item.delivery_status === 'partial'
          ? 'Parcial'
          : 'Pendiente';

      const itemName =
        safeText(item.item_name);

      const itemType =
        safeText(item.request_type)
          .replace(/_/g, ' ');

      const wrappedName =
        doc.splitTextToSize(
          itemName,
          columns[0].width - 4
        );

      const rowHeight =
        Math.max(
          11,
          wrappedName.length * 4 + 6
        );

      y = ensureSpace(
        y,
        rowHeight + 5
      );

      if (y === 30) {
        drawTableHeader();
      }

      doc.setDrawColor(226, 232, 240);
      doc.rect(
        tableX,
        y,
        tableWidth,
        rowHeight
      );

      let cellX = tableX;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(30, 41, 59);

      doc.text(
        wrappedName,
        cellX + 2,
        y + 5
      );

      cellX += columns[0].width;

      doc.text(
        itemType,
        cellX + 2,
        y + 5
      );

      cellX += columns[1].width;

      doc.text(
        String(requested),
        cellX + columns[2].width - 2,
        y + 5,
        { align: 'right' }
      );

      cellX += columns[2].width;

      doc.text(
        String(received),
        cellX + columns[3].width - 2,
        y + 5,
        { align: 'right' }
      );

      cellX += columns[3].width;

      doc.text(
        String(pending),
        cellX + columns[4].width - 2,
        y + 5,
        { align: 'right' }
      );

      cellX += columns[4].width;

      doc.setFont('helvetica', 'bold');
      doc.text(
        itemStatus,
        cellX + 2,
        y + 5
      );

      y += rowHeight;
    });

    // =====================================================
    // RESUMEN
    // =====================================================

    y += 7;
    y = ensureSpace(y, 28);

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(
      margin,
      y,
      contentWidth,
      22,
      3,
      3,
      'F'
    );

    const summaryColumns = [
      {
        label: 'TOTAL SOLICITADO',
        value: totalRequested,
        x: margin + 5
      },
      {
        label: 'TOTAL RECIBIDO',
        value: totalReceived,
        x: margin + 70
      },
      {
        label: 'TOTAL PENDIENTE',
        value: totalPending,
        x: margin + 135
      }
    ];

    summaryColumns.forEach((summary) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);

      doc.text(
        summary.label,
        summary.x,
        y + 7
      );

      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);

      doc.text(
        String(summary.value),
        summary.x,
        y + 15
      );
    });

    y += 31;

    // =====================================================
    // JUSTIFICACIÓN
    // =====================================================

    y = sectionTitle(
      'JUSTIFICACIÓN Y OBSERVACIONES',
      y
    );

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(
      'JUSTIFICACIÓN GENERAL',
      margin,
      y
    );

    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 41, 59);

    const justification =
      doc.splitTextToSize(
        safeText(request.justification),
        contentWidth
      );

    y = ensureSpace(
      y,
      justification.length * 4.5 + 8
    );

    doc.text(
      justification,
      margin,
      y
    );

    y +=
      justification.length * 4.5 +
      8;

    if (request.notes) {
      y = ensureSpace(y, 15);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);

      doc.text(
        'NOTAS GENERALES',
        margin,
        y
      );

      y += 5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);

      const notes =
        doc.splitTextToSize(
          safeText(request.notes),
          contentWidth
        );

      y = ensureSpace(
        y,
        notes.length * 4.5 + 8
      );

      doc.text(
        notes,
        margin,
        y
      );

      y +=
        notes.length * 4.5 +
        8;
    }

    // =====================================================
    // TRAZABILIDAD
    // =====================================================

    y = sectionTitle(
      'TRAZABILIDAD DEL PEDIDO',
      y
    );

    const trace = [
      {
        title: 'Solicitud creada',
        person: request.requester_name,
        date: request.created_at
      },
      {
        title: 'Pedido aprobado',
        person: request.approved_by,
        date: request.approved_date
      },
      {
        title: 'Pedido preparado',
        person: request.prepared_by,
        date: request.prepared_date
      },
      {
        title: 'Listo para recibir',
        person: request.ready_by,
        date: request.ready_date
      },
      {
        title: 'Recepción completada',
        person: request.delivered_by,
        date: request.delivered_date
      }
    ];

    trace.forEach((event) => {
      y = ensureSpace(y, 17);

      doc.setFillColor(51, 65, 85);
      doc.circle(
        margin + 3,
        y,
        2,
        'F'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(30, 41, 59);

      doc.text(
        event.title,
        margin + 10,
        y + 1
      );

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);

      doc.text(
        event.person
          ? `Responsable: ${event.person}`
          : 'Pendiente',
        margin + 10,
        y + 6
      );

      if (event.date) {
        doc.text(
          formatDate(event.date),
          pageWidth - margin,
          y + 6,
          { align: 'right' }
        );
      }

      y += 15;
    });

    if (request.delivery_notes) {
      y += 3;
      y = ensureSpace(y, 20);

      doc.setFillColor(255, 251, 235);
      doc.roundedRect(
        margin,
        y,
        contentWidth,
        15,
        2,
        2,
        'F'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(146, 64, 14);

      doc.text(
        'OBSERVACIÓN DE RECEPCIÓN',
        margin + 4,
        y + 5
      );

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);

      const deliveryNotes =
        doc.splitTextToSize(
          safeText(request.delivery_notes),
          contentWidth - 8
        );

      doc.text(
        deliveryNotes,
        margin + 4,
        y + 10
      );

      y += Math.max(
        18,
        deliveryNotes.length * 4 + 12
      );
    }

    // =====================================================
    // HISTORIAL DE AUDITORÍA
    // =====================================================

    if (
      Array.isArray(detailHistory) &&
      detailHistory.length > 0
    ) {
      y += 5;

      y = sectionTitle(
        'HISTORIAL DE MOVIMIENTOS',
        y
      );

      detailHistory.forEach((event) => {
        y = ensureSpace(y, 25);

        const title =
          actionLabel(event.action);

        const userName =
          safeText(event.user_name);

        const timestamp =
          formatDate(event.timestamp);

        const details =
          event.details || {};

        const previousStatus =
          details.previous_status
            ? statusLabel(
                details.previous_status
              )
            : null;

        const newStatus =
          details.new_status
            ? statusLabel(
                details.new_status
              )
            : null;

        const detailLines = [];

        if (
          previousStatus &&
          newStatus
        ) {
          detailLines.push(
            `Estado: ${previousStatus} → ${newStatus}`
          );
        }

        if (
          details.total_requested !==
          undefined
        ) {
          detailLines.push(
            `Solicitado: ${details.total_requested}`
          );
        }

        if (
          details.total_received !==
          undefined
        ) {
          detailLines.push(
            `Recibido: ${details.total_received}`
          );
        }

        if (
          details.total_pending !==
          undefined
        ) {
          detailLines.push(
            `Pendiente: ${details.total_pending}`
          );
        }

        doc.setFillColor(
          248,
          250,
          252
        );

        doc.roundedRect(
          margin,
          y,
          contentWidth,
          10,
          2,
          2,
          'F'
        );

        doc.setFont(
          'helvetica',
          'bold'
        );
        doc.setFontSize(8);
        doc.setTextColor(
          30,
          41,
          59
        );

        doc.text(
          title,
          margin + 4,
          y + 4
        );

        doc.setFont(
          'helvetica',
          'normal'
        );
        doc.setFontSize(7);
        doc.setTextColor(
          100,
          116,
          139
        );

        doc.text(
          userName,
          margin + 4,
          y + 8
        );

        doc.text(
          timestamp,
          pageWidth - margin - 4,
          y + 6,
          { align: 'right' }
        );

        y += 14;

        if (detailLines.length > 0) {
          doc.setFont(
            'helvetica',
            'normal'
          );
          doc.setFontSize(7.5);
          doc.setTextColor(
            71,
            85,
            105
          );

          detailLines.forEach(
            (line) => {
              y = ensureSpace(
                y,
                7
              );

              doc.text(
                line,
                margin + 6,
                y
              );

              y += 4;
            }
          );
        }

        if (
          Array.isArray(details.items) &&
          details.items.length > 0
        ) {
          y += 2;

          doc.setFont(
            'helvetica',
            'bold'
          );
          doc.setFontSize(7);
          doc.setTextColor(
            71,
            85,
            105
          );

          doc.text(
            'Detalle de recepción:',
            margin + 6,
            y
          );

          y += 4;

          details.items.forEach(
            (item) => {
              y = ensureSpace(
                y,
                7
              );

              const itemName =
                safeText(
                  item.item_name ||
                    `Ítem ${
                      Number(
                        item.item_index ??
                          0
                      ) + 1
                    }`
                );

              const receivedNow =
                Number(
                  item.quantity_received_now ??
                    item.quantity_received ??
                    item.quantity ??
                    0
                );

              const totalItem =
                item.quantity_received_total ??
                item.total_received;

              const pendingItem =
                item.quantity_pending;

              let line =
                `• ${itemName}: +${receivedNow} recibido`;

              if (
                totalItem !==
                undefined
              ) {
                line +=
                  ` · Total: ${totalItem}`;
              }

              if (
                pendingItem !==
                undefined
              ) {
                line +=
                  ` · Pendiente: ${pendingItem}`;
              }

              const wrapped =
                doc.splitTextToSize(
                  line,
                  contentWidth - 12
                );

              doc.setFont(
                'helvetica',
                'normal'
              );
              doc.setFontSize(7);
              doc.setTextColor(
                71,
                85,
                105
              );

              doc.text(
                wrapped,
                margin + 8,
                y
              );

              y +=
                wrapped.length * 3.5 +
                2;
            }
          );
        }

        if (details.notes) {
          y += 2;
          y = ensureSpace(y, 10);

          doc.setFont(
            'helvetica',
            'italic'
          );
          doc.setFontSize(7);
          doc.setTextColor(
            100,
            116,
            139
          );

          const noteText =
            doc.splitTextToSize(
              `Observación: ${details.notes}`,
              contentWidth - 12
            );

          doc.text(
            noteText,
            margin + 6,
            y
          );

          y +=
            noteText.length * 3.5 +
            4;
        }

        y += 4;
      });
    }

    // =====================================================
    // PIE DE PÁGINA
    // =====================================================

    addFooter();

    const orderNumber = safeText(
      request.order_number ||
        request.id ||
        'pedido'
    ).replace(
      /[^a-zA-Z0-9_-]/g,
      '_'
    );

    doc.save(
      `MAQControl_${orderNumber}.pdf`
    );

    toast.success(
      'PDF generado correctamente'
    );
  };
  const exportPendingPurchasesPDF = () => {
    const pendingRequests = requests.filter(
      (request) => request.status === 'pending'
    );

    if (pendingRequests.length === 0) {
      toast.info('No hay pedidos pendientes para exportar.');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;

    const safeText = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      return String(value);
    };

    const priorityLabels = {
      low: 'Baja',
      normal: 'Normal',
      high: 'Alta',
      urgent: 'Urgente'
    };

    const typeLabels = {
      repuestos: 'Repuestos',
      materiales: 'Materiales',
      herramientas: 'Herramientas',
      combustible: 'Combustible',
      otros: 'Otros'
    };

    const getItems = (request) => {
      if (Array.isArray(request.items) && request.items.length > 0) {
        return request.items;
      }

      return [{
        item_name: request.item_name || '',
        request_type: request.request_type || '',
        quantity_requested: request.quantity || 0,
        unit: request.unit || 'unidad',
        justification: request.justification || ''
      }];
    };

    const addHeader = (firstPage = false) => {
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, firstPage ? 40 : 20, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');

      if (firstPage) {
        doc.setFontSize(20);
        doc.text('MAQControl', margin, 15);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(
          'Sistema de Gestión de Maquinaria y Operaciones',
          margin,
          23
        );

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(
          'RESUMEN DE COMPRAS',
          pageWidth - margin,
          15,
          { align: 'right' }
        );

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(
          'Pedidos pendientes',
          pageWidth - margin,
          23,
          { align: 'right' }
        );
      } else {
        doc.setFontSize(11);
        doc.text('MAQControl', margin, 12);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(
          'Resumen de Compras · Pedidos pendientes',
          pageWidth - margin,
          12,
          { align: 'right' }
        );
      }
    };

    const ensureSpace = (y, needed = 20) => {
      if (y + needed > pageHeight - 20) {
        doc.addPage();
        addHeader(false);
        return 28;
      }
      return y;
    };

    const drawSectionTitle = (title, y) => {
      y = ensureSpace(y, 14);

      doc.setFillColor(30, 41, 59);
      doc.roundedRect(
        margin,
        y,
        contentWidth,
        8,
        2,
        2,
        'F'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text(title, margin + 4, y + 5.5);

      return y + 13;
    };

    const drawFooter = () => {
      const pageCount = doc.getNumberOfPages();

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        doc.setDrawColor(226, 232, 240);
        doc.line(
          margin,
          pageHeight - 13,
          pageWidth - margin,
          pageHeight - 13
        );

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);

        doc.text(
          'MAQControl · Documento para Compras',
          margin,
          pageHeight - 7
        );

        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth - margin,
          pageHeight - 7,
          { align: 'right' }
        );
      }
    };

    const consolidated = {};

    pendingRequests.forEach((request) => {
      getItems(request).forEach((item) => {
        const name = safeText(item.item_name).trim();
        if (!name || name === '-') return;

        const unit = safeText(item.unit || 'unidad').trim();
        const type = safeText(
          typeLabels[item.request_type] || item.request_type
        ).trim();

        const quantity = Number(
          item.quantity_requested ??
          item.quantity ??
          0
        );

        const key = `${name.toLowerCase()}|||${unit.toLowerCase()}`;

        if (!consolidated[key]) {
          consolidated[key] = {
            name,
            unit,
            type,
            total: 0,
            requests: []
          };
        }

        consolidated[key].total += quantity;

        consolidated[key].requests.push({
          order: request.order_number || request.id,
          requester: request.requester_name || 'Sin solicitante',
          quantity,
          justification: item.justification || request.justification || '',
          priority:
            priorityLabels[request.priority] ||
            request.priority ||
            'Normal'
        });
      });
    });

    const products = Object.values(consolidated);

    addHeader(true);

    let y = 50;

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(
      'RESUMEN DE COMPRAS PENDIENTES',
      margin,
      y
    );

    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);

    doc.text(
      `Generado: ${new Date().toLocaleString('es-BO')}`,
      margin,
      y
    );

    y += 10;

    const kpiWidth = (contentWidth - 6) / 3;

    const kpis = [
      ['PEDIDOS PENDIENTES', pendingRequests.length],
      ['PRODUCTOS', products.length],
      [
        'SOLICITANTES',
        new Set(
          pendingRequests.map(
            (request) => request.requester_name || 'Sin solicitante'
          )
        ).size
      ]
    ];

    kpis.forEach((kpi, index) => {
      const x = margin + index * (kpiWidth + 3);

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(
        x,
        y,
        kpiWidth,
        20,
        3,
        3,
        'FD'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(100, 116, 139);
      doc.text(kpi[0], x + 4, y + 6);

      doc.setFontSize(13);
      doc.setTextColor(15, 23, 42);
      doc.text(String(kpi[1]), x + 4, y + 15);
    });

    y += 29;

    y = drawSectionTitle(
      '1. RESUMEN CONSOLIDADO DE COMPRAS',
      y
    );

    const drawTableHeader = () => {
      doc.setFillColor(241, 245, 249);
      doc.rect(margin, y, contentWidth, 9, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(51, 65, 85);

      doc.text('PRODUCTO', margin + 3, y + 6);
      doc.text('TIPO', margin + 72, y + 6);
      doc.text('CANTIDAD', margin + 118, y + 6);
      doc.text('UNIDAD', margin + 143, y + 6);
      doc.text('PEDIDOS', margin + 169, y + 6);

      y += 9;
    };

    drawTableHeader();

    products.forEach((product) => {
      y = ensureSpace(y, 12);

      if (y === 28) {
        drawTableHeader();
      }

      const productLines = doc.splitTextToSize(
        product.name,
        64
      );

      doc.setDrawColor(226, 232, 240);
      doc.line(
        margin,
        y + Math.max(10, productLines.length * 4),
        pageWidth - margin,
        y + Math.max(10, productLines.length * 4)
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(15, 23, 42);

      doc.text(
        productLines.slice(0, 2),
        margin + 3,
        y + 5
      );

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);

      doc.text(
        product.type,
        margin + 72,
        y + 5
      );

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);

      doc.text(
        String(product.total),
        margin + 118,
        y + 5
      );

      doc.setFont('helvetica', 'normal');

      doc.text(
        product.unit,
        margin + 143,
        y + 5
      );

      doc.text(
        String(product.requests.length),
        margin + 169,
        y + 5
      );

      y += Math.max(10, productLines.length * 4);
    });

    y += 7;

    y = drawSectionTitle(
      '2. DETALLE DE SOLICITUDES',
      y
    );

    products.forEach((product) => {
      y = ensureSpace(y, 30);

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(
        margin,
        y,
        contentWidth,
        10,
        2,
        2,
        'FD'
      );

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);

      doc.text(
        product.name,
        margin + 4,
        y + 6.5
      );

      doc.setFontSize(7);
      doc.setTextColor(71, 85, 105);

      doc.text(
        `TOTAL A COMPRAR: ${product.total} ${product.unit}`,
        pageWidth - margin - 4,
        y + 6.5,
        { align: 'right' }
      );

      y += 13;

      product.requests.forEach((detail) => {
        y = ensureSpace(y, 25);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(15, 23, 42);

        doc.text(
          safeText(detail.requester),
          margin + 3,
          y + 4
        );

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);

        doc.text(
          `Pedido: ${safeText(detail.order)}`,
          margin + 65,
          y + 4
        );

        doc.text(
          `Cantidad: ${detail.quantity} ${product.unit}`,
          margin + 125,
          y + 4
        );

        doc.text(
          `Prioridad: ${safeText(detail.priority)}`,
          margin + 3,
          y + 10
        );

        const justification = safeText(
          detail.justification
        );

        const justificationLines = doc.splitTextToSize(
          `Justificación: ${justification}`,
          contentWidth - 6
        );

        doc.text(
          justificationLines.slice(0, 2),
          margin + 3,
          y + 16
        );

        y += Math.max(
          22,
          justificationLines.slice(0, 2).length * 4 + 17
        );

        doc.setDrawColor(226, 232, 240);
        doc.line(
          margin + 3,
          y - 2,
          pageWidth - margin - 3,
          y - 2
        );
      });

      y += 4;
    });

    y = ensureSpace(y, 42);
    y += 4;

    y = drawSectionTitle(
      '3. CONTROL DE COMPRA',
      y
    );

    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(
      margin,
      y,
      contentWidth,
      28,
      3,
      3,
      'S'
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);

    doc.text(
      'Responsable de compras:',
      margin + 5,
      y + 8
    );

    doc.line(
      margin + 48,
      y + 8,
      pageWidth - margin - 5,
      y + 8
    );

    doc.text(
      'Fecha de compra:',
      margin + 5,
      y + 17
    );

    doc.line(
      margin + 36,
      y + 17,
      margin + 82,
      y + 17
    );

    doc.text(
      'Observaciones:',
      margin + 92,
      y + 17
    );

    doc.line(
      margin + 122,
      y + 17,
      pageWidth - margin - 5,
      y + 17
    );

    drawFooter();

    doc.save(
      `Resumen_Compras_Pendientes_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`
    );

    toast.success(
      `PDF generado con ${pendingRequests.length} pedido(s) pendiente(s).`
    );
  };

  const closeDetailDialog = () => {
    setDetailDialogOpen(false);
    setDetailRequest(null);
    setDetailHistory([]);
  };

  const openReceiptDialog = (request) => {
    const items = Array.isArray(request.items) ? request.items : [];

    setReceiptRequest(request);
    setReceiptItems(
      items.map((item, index) => ({
        item_index: index,
        item_name: item.item_name,
        unit: item.unit,
        quantity_requested: Number(
          item.quantity_requested ?? item.quantity ?? 0
        ),
        quantity_received: 0,
        quantity_delivered: Number(item.quantity_delivered ?? 0),
        quantity_pending: Number(
          item.quantity_pending ??
          Math.max(
            Number(item.quantity_requested ?? item.quantity ?? 0) -
            Number(item.quantity_delivered ?? 0),
            0
          )
        )
      }))
    );
    setReceiptNotes('');
    setReceiptDialogOpen(true);
  };

  const closeReceiptDialog = () => {
    setReceiptDialogOpen(false);
    setReceiptRequest(null);
    setReceiptItems([]);
    setReceiptNotes('');
  };

  const handleConfirmReceipt = async () => {
    if (!receiptRequest) return;

    const itemsToSend = receiptItems
      .filter((item) => Number(item.quantity_received) > 0)
      .map((item) => ({
        item_index: item.item_index,
        quantity_received: Number(item.quantity_received)
      }));

    if (itemsToSend.length === 0) {
      toast.error('Debe indicar al menos un artículo recibido');
      return;
    }

    for (const item of receiptItems) {
      const receivedNow = Number(item.quantity_received) || 0;
      const pending = Number(item.quantity_pending) || 0;

      if (receivedNow < 0) {
        toast.error(`La cantidad recibida de "${item.item_name}" no puede ser negativa`);
        return;
      }

      if (receivedNow > pending) {
        toast.error(
          `No puede recibir más de lo pendiente de "${item.item_name}". Pendiente: ${pending}`
        );
        return;
      }
    }

    try {
      const response = await axios.patch(
        `${API}/material-requests/${receiptRequest.id}/confirm-receipt`,
        {
          items: itemsToSend,
          notes: receiptNotes.trim() || null
        }
      );

      toast.success(
        response.data?.status === 'delivered'
          ? 'Recepción confirmada completamente'
          : 'Recepción parcial confirmada'
      );

      closeReceiptDialog();
      fetchRequests();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'Error al confirmar la recepción'
      );
    }
  };

  const resetForm = () => {
    setFormData({
      request_type: 'repuestos',
      item_name: '',
      quantity: 1,
      unit: 'unidad',
      justification: '',
      notes: ''
    });

    setCart([]);
    setPriority('normal');
    setGeneralJustification('');
    setGeneralNotes('');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready: 'bg-purple-100 text-purple-800',
      ready: 'bg-purple-100 text-purple-800',
      partial: 'bg-orange-100 text-orange-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const labels = {
      pending: 'Pendiente',
      approved: 'Aprobado',
      preparing: 'En preparación',
      ready: 'Listo para recibir',
      partial: 'Entrega parcial',
      delivered: 'Entrega confirmada',
      rejected: 'Rechazado'
    };

    return (
      <span className={`status-badge ${badges[status] || 'bg-slate-100 text-slate-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: 'bg-blue-100 text-blue-800',
      normal: 'bg-slate-100 text-slate-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    const labels = {
      low: 'Baja',
      normal: 'Normal',
      high: 'Alta',
      urgent: 'Urgente'
    };
    return (
      <span className={`status-badge ${badges[priority]}`}>
        {labels[priority]}
      </span>
    );
  };

  return (
    <div data-testid="material-requests-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Pedidos de Materiales
          </h1>
          <p className="text-slate-600 mt-2">Solicitudes de insumos, repuestos y materiales</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={exportPendingPurchasesPDF}
            className="bg-slate-800 hover:bg-slate-700 text-white"
          >
            📄 Resumen de Compras
          </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-request-button" className="btn-primary">
                <Plus size={20} className="mr-2" />
                Nuevo Pedido
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0">
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Nuevo Pedido de Material
              </DialogTitle>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="space-y-5 overflow-y-auto pr-2 flex-1 min-h-0"
            >

              {/* Agregar artículo */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  Agregar artículo al pedido
                </h3>

                <div className="grid grid-cols-2 gap-4">

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Tipo de Pedido
                    </label>
                    <select
                      value={formData.request_type}
                      onChange={(e) =>
                        setFormData({ ...formData, request_type: e.target.value })
                      }
                      className="input-field"
                    >
                      <option value="repuestos">Repuestos</option>
                      <option value="combustible">Combustible</option>
                      <option value="aceites">Aceites y Lubricantes</option>
                      <option value="herramientas">Herramientas</option>
                      <option value="insumos">Insumos Generales</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Artículo / Material
                    </label>
                    <input
                      type="text"
                      value={formData.item_name}
                      onChange={(e) =>
                        setFormData({ ...formData, item_name: e.target.value })
                      }
                      className="input-field"
                      placeholder="Ej. Filtro de aceite"
                    />
                  </div>

                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Cantidad
                    </label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={formData.quantity}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          quantity: e.target.value
                        })
                      }
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Unidad
                    </label>
                    <select
                      value={formData.unit}
                      onChange={(e) =>
                        setFormData({ ...formData, unit: e.target.value })
                      }
                      className="input-field"
                    >
                      <option value="unidad">Unidad</option>
                      <option value="litros">Litros</option>
                      <option value="kg">Kilogramos</option>
                      <option value="metros">Metros</option>
                      <option value="cajas">Cajas</option>
                    </select>
                  </div>

                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Justificación del artículo
                  </label>
                  <textarea
                    value={formData.justification}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        justification: e.target.value
                      })
                    }
                    className="input-field"
                    rows={2}
                    placeholder="Explique por qué necesita este artículo..."
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notas del artículo (Opcional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        notes: e.target.value
                      })
                    }
                    className="input-field"
                    rows={2}
                    placeholder="Marca, modelo, especificaciones, etc."
                  />
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    onClick={addItemToCart}
                    className="btn-primary"
                  >
                    <Plus size={18} className="mr-2" />
                    Agregar al pedido
                  </Button>
                </div>
              </div>

              {/* Carrito */}
              {cart.length > 0 && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-800">
                      Artículos del pedido
                    </h3>

                    <span className="text-sm font-medium text-slate-600">
                      {cart.length} {cart.length === 1 ? 'artículo' : 'artículos'}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {cart.map((item, index) => (
                      <div
                        key={`${item.item_name}-${index}`}
                        className="border rounded-lg p-3 bg-white flex items-start justify-between gap-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-semibold text-slate-800">
                              {item.item_name}
                            </p>

                            <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                              {item.quantity} {item.unit}
                            </span>
                          </div>

                          <p className="text-xs text-slate-500 mt-1">
                            Tipo: {item.request_type}
                          </p>

                          {item.justification && (
                            <p className="text-sm text-slate-600 mt-2">
                              <span className="font-medium">Justificación:</span>{' '}
                              {item.justification}
                            </p>
                          )}

                          {item.notes && (
                            <p className="text-sm text-slate-500 mt-1">
                              <span className="font-medium">Nota:</span>{' '}
                              {item.notes}
                            </p>
                          )}
                        </div>

                        <Button
                          type="button"
                          onClick={() => removeItemFromCart(index)}
                          className="btn-secondary"
                          title="Eliminar artículo"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Datos generales */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  Datos generales del pedido
                </h3>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Prioridad
                  </label>

                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="input-field"
                  >
                    <option value="low">Baja</option>
                    <option value="normal">Normal</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Justificación general del pedido
                  </label>

                  <textarea
                    value={generalJustification}
                    onChange={(e) => setGeneralJustification(e.target.value)}
                    className="input-field"
                    rows={3}
                    placeholder="Explique la necesidad general de este pedido..."
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Notas generales (Opcional)
                  </label>

                  <textarea
                    value={generalNotes}
                    onChange={(e) => setGeneralNotes(e.target.value)}
                    className="input-field"
                    rows={2}
                    placeholder="Observaciones generales del pedido..."
                  />
                </div>
              </div>

              {/* Acciones */}
              <div className="flex justify-end space-x-3 pt-2">
                <Button
                  type="button"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={cart.length === 0}
                >
                  Enviar Pedido
                </Button>
              </div>

            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Centro de Solicitudes */}
      <div className="mb-6">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 text-xl">
                📋
              </div>
              <div>
                <h1
                  className="text-3xl md:text-4xl font-bold text-slate-800"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  Centro de Solicitudes
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  Gestión y seguimiento de pedidos de materiales, repuestos e insumos
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Indicadores */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <button
            onClick={() => setFilter('all')}
            className={`text-left rounded-xl border p-4 transition-all ${
              filter === 'all'
                ? 'border-slate-800 bg-slate-800 text-white shadow-md'
                : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
            }`}
          >
            <p className={`text-xs font-semibold uppercase tracking-wide ${
              filter === 'all' ? 'text-slate-300' : 'text-slate-500'
            }`}>
              Todos
            </p>
            <p className="text-2xl font-bold mt-1">
              {requests.length}
            </p>
            <p className={`text-xs mt-1 ${
              filter === 'all' ? 'text-slate-300' : 'text-slate-400'
            }`}>
              pedidos registrados
            </p>
          </button>

          <button
            onClick={() => setFilter('pending')}
            className={`text-left rounded-xl border p-4 transition-all ${
              filter === 'pending'
                ? 'border-amber-400 bg-amber-50 shadow-md'
                : 'border-slate-200 bg-white hover:border-amber-300 hover:shadow-sm'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
              Pendientes
            </p>
            <p className="text-2xl font-bold mt-1 text-slate-800">
              {requests.filter((r) => r.status === 'pending').length}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              requieren aprobación
            </p>
          </button>

          <button
            onClick={() => setFilter('approved')}
            className={`text-left rounded-xl border p-4 transition-all ${
              ['approved', 'preparing', 'ready', 'partial'].includes(filter)
                ? 'border-blue-400 bg-blue-50 shadow-md'
                : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
              En proceso
            </p>
            <p className="text-2xl font-bold mt-1 text-slate-800">
              {requests.filter((r) =>
                ['approved', 'preparing', 'ready', 'partial'].includes(r.status)
              ).length}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              pedidos en gestión
            </p>
          </button>

          <button
            onClick={() => setFilter('delivered')}
            className={`text-left rounded-xl border p-4 transition-all ${
              filter === 'delivered'
                ? 'border-emerald-400 bg-emerald-50 shadow-md'
                : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
              Completados
            </p>
            <p className="text-2xl font-bold mt-1 text-slate-800">
              {requests.filter((r) => r.status === 'delivered').length}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              recepción confirmada
            </p>
          </button>

          <button
            onClick={() => setFilter('rejected')}
            className={`text-left rounded-xl border p-4 transition-all ${
              filter === 'rejected'
                ? 'border-red-400 bg-red-50 shadow-md'
                : 'border-slate-200 bg-white hover:border-red-300 hover:shadow-sm'
            }`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
              Rechazados
            </p>
            <p className="text-2xl font-bold mt-1 text-slate-800">
              {requests.filter((r) => r.status === 'rejected').length}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              solicitudes rechazadas
            </p>
          </button>
        </div>

        {/* Búsqueda y filtros */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="relative flex-1">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  🔎
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por número de pedido, solicitante, material o tipo..."
                  className="w-full h-11 pl-10 pr-10 border border-slate-300 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                    title="Limpiar búsqueda"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="text-sm text-slate-500 whitespace-nowrap px-2">
                <span className="font-bold text-slate-800">
                  {filteredRequests.length}
                </span>
                {' '}resultado{filteredRequests.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="p-3 bg-slate-50/70">
            <div className="flex flex-wrap gap-2">
              {[
                ['all', 'Todos'],
                ['pending', 'Pendientes'],
                ['approved', 'Aprobados'],
                ['preparing', 'En preparación'],
                ['ready', 'Listos para recibir'],
                ['partial', 'Entregas parciales'],
                ['delivered', 'Confirmados'],
                ['rejected', 'Rechazados']
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setFilter(value)}
                  className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-all ${
                    filter === value
                      ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Listado */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <div className="text-3xl mb-3">⏳</div>
          <p className="text-sm font-medium text-slate-600">
            Cargando pedidos...
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const items =
              Array.isArray(request.items) && request.items.length > 0
                ? request.items
                : [{
                    request_type: request.request_type || 'otros',
                    item_name: request.item_name || '',
                    quantity: request.quantity || 0,
                    unit: request.unit || 'unidad',
                    justification: request.justification || '',
                    notes: request.notes || ''
                  }];

            const progressSteps = [
              {
                key: 'pending',
                label: 'Solicitud',
                active: true
              },
              {
                key: 'approved',
                label: 'Aprobación',
                active: ['approved', 'preparing', 'ready', 'partial', 'delivered'].includes(request.status)
              },
              {
                key: 'preparing',
                label: 'Preparación',
                active: ['preparing', 'ready', 'partial', 'delivered'].includes(request.status)
              },
              {
                key: 'ready',
                label: 'Entrega',
                active: ['ready', 'partial', 'delivered'].includes(request.status)
              },
              {
                key: 'delivered',
                label: 'Confirmación',
                active: request.status === 'delivered'
              }
            ];

            return (
              <div
                key={request.id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Cabecera del pedido */}
                <div className="px-4 sm:px-5 py-4 border-b border-slate-100">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="px-2.5 py-1 rounded-md bg-slate-800 text-white text-xs font-bold tracking-wide">
                          {request.order_number || 'SIN NÚMERO'}
                        </span>

                        {getStatusBadge(request.status)}
                        {getPriorityBadge(request.priority)}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-5 gap-y-1">
                        <h3 className="text-base sm:text-lg font-bold text-slate-800">
                          Pedido de Material
                        </h3>

                        <p className="text-sm text-slate-500">
                          Solicitado por:{' '}
                          <span className="font-semibold text-slate-700">
                            {request.requester_name || 'Sin solicitante'}
                          </span>
                        </p>

                        <p className="text-sm text-slate-500">
                          {request.created_at
                            ? new Date(request.created_at).toLocaleDateString('es-BO')
                            : '-'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <button
                        onClick={() => openDetailDialog(request)}
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition-colors"
                        title="Ver detalle completo del pedido"
                      >
                        Ver detalle
                      </button>

                      {user.role !== 'operator' && (
                        <>
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(request.id)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-semibold transition-colors"
                                title="Aprobar"
                              >
                                <CheckCircle size={17} />
                                <span className="hidden sm:inline">Aprobar</span>
                              </button>

                              <button
                                onClick={() => handleReject(request.id)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-sm font-semibold transition-colors"
                                title="Rechazar"
                              >
                                <XCircle size={17} />
                                <span className="hidden sm:inline">Rechazar</span>
                              </button>
                            </>
                          )}

                          {request.status === 'approved' && (
                            <button
                              onClick={() => handlePrepare(request.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                              title="Iniciar preparación"
                            >
                              <PlayCircle size={17} />
                              Iniciar preparación
                            </button>
                          )}

                          {request.status === 'preparing' && (
                            <button
                              onClick={() => handleReady(request.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
                              title="Marcar como listo"
                            >
                              <PackageCheck size={17} />
                              Marcar como listo
                            </button>
                          )}

                          {(request.status === 'ready' || request.status === 'partial') &&
                            request.requester_id === user.id && (
                              <button
                                onClick={() => openReceiptDialog(request)}
                                className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors"
                                title="Confirmar recepción"
                              >
                                <Truck size={17} />
                                Confirmar recepción
                              </button>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Progreso */}
                <div className="px-4 sm:px-5 py-4 bg-slate-50/60 border-b border-slate-100">
                  <div className="flex items-center">
                    {progressSteps.map((step, index) => (
                      <React.Fragment key={step.key}>
                        <div className="flex flex-col items-center min-w-0">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                              step.active
                                ? 'bg-orange-500 border-orange-500 text-white'
                                : 'bg-white border-slate-300 text-slate-400'
                            }`}
                          >
                            {step.active ? '✓' : index + 1}
                          </div>
                          <span
                            className={`text-[10px] sm:text-xs mt-1.5 text-center ${
                              step.active
                                ? 'font-semibold text-slate-700'
                                : 'text-slate-400'
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>

                        {index < progressSteps.length - 1 && (
                          <div
                            className={`h-0.5 flex-1 mx-1 sm:mx-2 mt-[-16px] ${
                              progressSteps[index + 1].active
                                ? 'bg-orange-400'
                                : 'bg-slate-200'
                            }`}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Contenido */}
                <div className="px-4 sm:px-5 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                        Artículos solicitados
                      </p>
                      <p className="text-sm text-slate-700 mt-0.5">
                        {items.length} {items.length === 1 ? 'artículo' : 'artículos'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                    {items.map((item, index) => (
                      <div
                        key={`${request.id}-${index}`}
                        className="border border-slate-200 rounded-lg p-3 bg-white hover:border-slate-300 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 truncate">
                              {item.item_name || 'Artículo sin nombre'}
                            </p>

                            <p className="text-xs text-slate-500 mt-1 capitalize">
                              {(item.request_type || 'otros').replace('_', ' ')}
                            </p>
                          </div>

                          <div className="shrink-0 text-right">
                            <p className="text-xs text-slate-400">Cantidad</p>
                            <p className="text-sm font-bold text-slate-800">
                              {item.quantity} {item.unit}
                            </p>
                          </div>
                        </div>

                        {(item.justification || item.notes) && (
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            {item.justification && (
                              <p className="text-xs text-slate-600 line-clamp-2">
                                <span className="font-semibold">Justificación:</span>{' '}
                                {item.justification}
                              </p>
                            )}

                            {item.notes && (
                              <p className="text-xs text-slate-500 italic mt-1 line-clamp-2">
                                <span className="font-semibold not-italic">Nota:</span>{' '}
                                {item.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Seguimiento */}
                  {(request.approved_by ||
                    request.prepared_by ||
                    request.ready_by ||
                    request.delivered_by) && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                      {request.approved_by && (
                        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle size={15} className="text-emerald-600" />
                            <span className="text-[11px] font-bold uppercase text-emerald-700">
                              Aprobado por
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-slate-700">
                            {request.approved_by}
                          </p>
                          {request.approved_date && (
                            <p className="text-[11px] text-slate-500 mt-1">
                              {new Date(request.approved_date).toLocaleString('es-BO')}
                            </p>
                          )}
                        </div>
                      )}

                      {request.prepared_by && (
                        <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PlayCircle size={15} className="text-blue-600" />
                            <span className="text-[11px] font-bold uppercase text-blue-700">
                              Preparado por
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-slate-700">
                            {request.prepared_by}
                          </p>
                          {request.prepared_date && (
                            <p className="text-[11px] text-slate-500 mt-1">
                              {new Date(request.prepared_date).toLocaleString('es-BO')}
                            </p>
                          )}
                        </div>
                      )}

                      {request.ready_by && (
                        <div className="rounded-lg border border-purple-100 bg-purple-50/50 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <PackageCheck size={15} className="text-purple-600" />
                            <span className="text-[11px] font-bold uppercase text-purple-700">
                              Pedido listo por
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-slate-700">
                            {request.ready_by}
                          </p>
                          {request.ready_date && (
                            <p className="text-[11px] text-slate-500 mt-1">
                              {new Date(request.ready_date).toLocaleString('es-BO')}
                            </p>
                          )}
                        </div>
                      )}

                      {request.delivered_by && (
                        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Truck size={15} className="text-emerald-600" />
                            <span className="text-[11px] font-bold uppercase text-emerald-700">
                              Recepción confirmada
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-slate-700">
                            {request.delivered_by}
                          </p>
                          {request.delivered_date && (
                            <p className="text-[11px] text-slate-500 mt-1">
                              {new Date(request.delivered_date).toLocaleString('es-BO')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Justificación */}
                  {(request.justification || request.notes) && (
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {request.justification && (
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                            Justificación general
                          </p>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {request.justification}
                          </p>
                        </div>
                      )}

                      {request.notes && (
                        <div className="rounded-lg bg-amber-50/50 border border-amber-100 p-3">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1">
                            Notas generales
                          </p>
                          <p className="text-sm text-slate-700 leading-relaxed italic">
                            {request.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {filteredRequests.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                <AlertCircle size={28} className="text-slate-400" />
              </div>

              {searchTerm ? (
                <>
                  <p className="font-semibold text-slate-700">
                    No encontramos pedidos
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    No existen solicitudes que coincidan con "{searchTerm}".
                  </p>
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="mt-4 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold"
                  >
                    Limpiar búsqueda
                  </button>
                </>
              ) : (
                <>
                  <p className="font-semibold text-slate-700">
                    No hay pedidos de materiales
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    No existen solicitudes para el filtro seleccionado.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDetailDialog();
        }}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0 border-b pb-4">
            <DialogTitle
              className="text-xl"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Detalle del pedido {detailRequest?.order_number || ''}
            </DialogTitle>
          </DialogHeader>

          {detailRequest && (
            <div className="overflow-y-auto pr-2 flex-1 min-h-0 space-y-5">

              {/* CABECERA */}
              <div className="bg-slate-50 border rounded-lg p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">

                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      Pedido de material
                    </p>

                    <p className="text-2xl font-bold text-slate-800 mt-1">
                      {detailRequest.order_number || 'Sin número'}
                    </p>

                    <p className="text-sm text-slate-600 mt-2">
                      Solicitado por:{' '}
                      <span className="font-semibold">
                        {detailRequest.requester_name}
                      </span>
                    </p>

                    <p className="text-sm text-slate-600">
                      Fecha:{' '}
                      {detailRequest.created_at
                        ? new Date(detailRequest.created_at).toLocaleString()
                        : '-'}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-200 text-slate-700">
                      Prioridad: {detailRequest.priority || 'normal'}
                    </span>

                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                      {detailRequest.status === 'delivered'
                        ? 'ENTREGA CONFIRMADA'
                        : detailRequest.status === 'partial'
                        ? 'ENTREGA PARCIAL'
                        : detailRequest.status === 'ready'
                        ? 'LISTO PARA RECIBIR'
                        : detailRequest.status === 'preparing'
                        ? 'EN PREPARACIÓN'
                        : detailRequest.status === 'approved'
                        ? 'APROBADO'
                        : detailRequest.status === 'rejected'
                        ? 'RECHAZADO'
                        : 'PENDIENTE'}
                    </span>
                  </div>

                </div>
              </div>

              {/* MATERIALES */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-slate-800">
                    Resumen de materiales
                  </h3>

                  <span className="text-xs text-slate-500">
                    {Array.isArray(detailRequest.items)
                      ? detailRequest.items.length
                      : 0}{' '}
                    artículos
                  </span>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left p-3">Artículo</th>
                          <th className="text-left p-3">Tipo</th>
                          <th className="text-right p-3">Solicitado</th>
                          <th className="text-right p-3">Recibido</th>
                          <th className="text-right p-3">Pendiente</th>
                          <th className="text-center p-3">Estado</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(detailRequest.items || []).map((item, index) => {
                          const requested = Number(
                            item.quantity_requested ??
                            item.quantity ??
                            0
                          );

                          const received = Number(
                            item.quantity_delivered ?? 0
                          );

                          const pending = Number(
                            item.quantity_pending ??
                            Math.max(requested - received, 0)
                          );

                          const itemStatus =
                            item.delivery_status === 'complete'
                              ? 'Completo'
                              : item.delivery_status === 'partial'
                              ? 'Parcial'
                              : 'Pendiente';

                          return (
                            <tr
                              key={`${detailRequest.id}-detail-${index}`}
                              className="border-t"
                            >
                              <td className="p-3">
                                <p className="font-semibold text-slate-800">
                                  {item.item_name}
                                </p>

                                <p className="text-xs text-slate-500">
                                  Unidad: {item.unit}
                                </p>

                                {item.justification && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Justificación: {item.justification}
                                  </p>
                                )}
                              </td>

                              <td className="p-3">
                                <span className="capitalize">
                                  {(item.request_type || 'otros').replace('_', ' ')}
                                </span>
                              </td>

                              <td className="p-3 text-right font-semibold">
                                {requested}
                              </td>

                              <td className="p-3 text-right font-semibold text-emerald-700">
                                {received}
                              </td>

                              <td className="p-3 text-right">
                                <span
                                  className={
                                    pending > 0
                                      ? 'font-semibold text-orange-600'
                                      : 'text-slate-500'
                                  }
                                >
                                  {pending}
                                </span>
                              </td>

                              <td className="p-3 text-center">
                                <span
                                  className={
                                    itemStatus === 'Completo'
                                      ? 'text-xs font-semibold text-emerald-700'
                                      : itemStatus === 'Parcial'
                                      ? 'text-xs font-semibold text-orange-700'
                                      : 'text-xs font-semibold text-slate-500'
                                  }
                                >
                                  {itemStatus}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* TRAZABILIDAD ACTUAL */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  Trazabilidad del pedido
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="text-xs uppercase text-slate-500">
                      Solicitud
                    </p>
                    <p className="font-semibold text-slate-800 mt-1">
                      {detailRequest.requester_name}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {detailRequest.created_at
                        ? new Date(detailRequest.created_at).toLocaleString()
                        : '-'}
                    </p>
                  </div>

                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="text-xs uppercase text-slate-500">
                      Aprobación
                    </p>

                    <p className="font-semibold text-slate-800 mt-1">
                      {detailRequest.approved_by || 'Pendiente'}
                    </p>

                    <p className="text-xs text-slate-500 mt-1">
                      {detailRequest.approved_date
                        ? new Date(detailRequest.approved_date).toLocaleString()
                        : '-'}
                    </p>
                  </div>

                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="text-xs uppercase text-slate-500">
                      Preparación
                    </p>

                    <p className="font-semibold text-slate-800 mt-1">
                      {detailRequest.prepared_by || 'Pendiente'}
                    </p>

                    <p className="text-xs text-slate-500 mt-1">
                      {detailRequest.prepared_date
                        ? new Date(detailRequest.prepared_date).toLocaleString()
                        : '-'}
                    </p>
                  </div>

                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="text-xs uppercase text-slate-500">
                      Listo para recibir
                    </p>

                    <p className="font-semibold text-slate-800 mt-1">
                      {detailRequest.ready_by || 'Pendiente'}
                    </p>

                    <p className="text-xs text-slate-500 mt-1">
                      {detailRequest.ready_date
                        ? new Date(detailRequest.ready_date).toLocaleString()
                        : '-'}
                    </p>
                  </div>

                  <div className="border rounded-lg p-4 bg-emerald-50 md:col-span-2">
                    <p className="text-xs uppercase text-emerald-700">
                      Recepción
                    </p>

                    <p className="font-semibold text-slate-800 mt-1">
                      {detailRequest.delivered_by || 'Pendiente de confirmación'}
                    </p>

                    <p className="text-xs text-slate-500 mt-1">
                      {detailRequest.delivered_date
                        ? new Date(detailRequest.delivered_date).toLocaleString()
                        : '-'}
                    </p>

                    {detailRequest.delivery_notes && (
                      <p className="text-sm text-slate-600 mt-2">
                        Observación: {detailRequest.delivery_notes}
                      </p>
                    )}
                  </div>

                </div>
              </div>

                            {/* HISTORIAL DE AUDITORÍA */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  Historial de auditoría
                </h3>

                {detailHistory.length === 0 ? (
                  <div className="border rounded-lg p-4 bg-slate-50 text-sm text-slate-500">
                    No hay eventos registrados para este pedido.
                  </div>
                ) : (
                  <div className="relative ml-2">
                    <div className="absolute left-4 top-5 bottom-5 w-px bg-slate-200" />

                    <div className="space-y-5">
                      {detailHistory.map((event, index) => {
                        const isReceipt = event.action === 'confirm_receipt';
                        const isComplete =
                          isReceipt && event.details?.new_status === 'delivered';

                        let title = 'Evento registrado';
                        let icon = '•';
                        let iconClass = 'bg-slate-600';

                        if (event.action === 'prepare') {
                          title = 'Pedido preparado';
                          icon = '⚙';
                          iconClass = 'bg-blue-600';
                        } else if (event.action === 'ready') {
                          title = 'Pedido listo para recibir';
                          icon = '✓';
                          iconClass = 'bg-indigo-600';
                        } else if (isReceipt && isComplete) {
                          title = 'Recepción completada';
                          icon = '✓';
                          iconClass = 'bg-emerald-600';
                        } else if (isReceipt) {
                          title = 'Recepción parcial';
                          icon = '↗';
                          iconClass = 'bg-orange-500';
                        }

                        const statusLabel = (status) => {
                          if (status === 'approved') return 'Aprobado';
                          if (status === 'preparing') return 'En preparación';
                          if (status === 'ready') return 'Listo para recibir';
                          if (status === 'partial') return 'Recepción parcial';
                          if (status === 'delivered') return 'Recepción completada';
                          return status || '-';
                        };

                        return (
                          <div
                            key={event.id || index}
                            className="relative flex gap-4"
                          >
                            <div
                              className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white text-sm font-bold shadow-sm ${iconClass}`}
                            >
                              {icon}
                            </div>

                            <div className="flex-1 rounded-xl border bg-white p-4 shadow-sm">
                              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                                <div>
                                  <h4 className="font-semibold text-slate-800">
                                    {title}
                                  </h4>

                                  <p className="text-sm text-slate-500 mt-1">
                                    {event.user_name || 'Sistema'}
                                  </p>
                                </div>

                                <span className="text-xs text-slate-400">
                                  {event.timestamp
                                    ? new Date(event.timestamp).toLocaleString()
                                    : '-'}
                                </span>
                              </div>

                              {event.details && (
                                <div className="mt-3 space-y-3 text-sm">
                                  {event.details.previous_status &&
                                    event.details.new_status && (
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-slate-500">
                                          Estado:
                                        </span>

                                        <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700 font-medium">
                                          {statusLabel(
                                            event.details.previous_status
                                          )}
                                        </span>

                                        <span className="text-slate-400">
                                          →
                                        </span>

                                        <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700 font-medium">
                                          {statusLabel(
                                            event.details.new_status
                                          )}
                                        </span>
                                      </div>
                                    )}

                                  {isReceipt && (
                                    <div className="grid grid-cols-3 gap-2">
                                      <div className="rounded-lg bg-slate-50 p-3">
                                        <p className="text-xs text-slate-500">
                                          Solicitado
                                        </p>
                                        <p className="font-semibold text-slate-800">
                                          {event.details.total_requested ?? 0}
                                        </p>
                                      </div>

                                      <div className="rounded-lg bg-emerald-50 p-3">
                                        <p className="text-xs text-emerald-700">
                                          Recibido
                                        </p>
                                        <p className="font-semibold text-emerald-700">
                                          {event.details.total_received ?? 0}
                                        </p>
                                      </div>

                                      <div className="rounded-lg bg-orange-50 p-3">
                                        <p className="text-xs text-orange-700">
                                          Pendiente
                                        </p>
                                        <p className="font-semibold text-orange-700">
                                          {event.details.total_pending ?? 0}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {isReceipt &&
                                    Array.isArray(event.details.items) &&
                                    event.details.items.length > 0 && (
                                      <div className="rounded-lg border bg-slate-50 p-3">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                                          Detalle de esta recepción
                                        </p>

                                        <div className="space-y-2">
                                          {event.details.items.map(
                                            (item, itemIndex) => (
                                              <div
                                                key={itemIndex}
                                                className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 border-b last:border-b-0 pb-2 last:pb-0"
                                              >
                                                <span className="font-medium text-slate-700">
                                                  {item.item_name ||
                                                    'Ítem ' +
                                                      (item.item_index + 1)}
                                                </span>

                                                <span className="text-xs text-slate-500">
                                                  +
                                                  {item.quantity_received_now ??
                                                    0}{' '}
                                                  recibidas
                                                  {' · Total acumulado: '}
                                                  {item.quantity_received_total ??
                                                    0}
                                                  {' · Pendientes: '}
                                                  {item.quantity_pending ?? 0}
                                                </span>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  {event.details.notes && (
                                    <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-sm text-slate-700">
                                      <span className="font-semibold">
                                        Observación:
                                      </span>{' '}
                                      {event.details.notes}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>


              {/* JUSTIFICACIÓN Y NOTAS */}
              <div className="grid grid-cols-1 gap-4">

                <div className="border rounded-lg p-4">
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    Justificación general
                  </p>

                  <p className="text-sm text-slate-700 bg-slate-50 rounded p-3">
                    {detailRequest.justification || '-'}
                  </p>
                </div>

                {detailRequest.notes && (
                  <div className="border rounded-lg p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">
                      Notas generales
                    </p>

                    <p className="text-sm text-slate-700 bg-slate-50 rounded p-3 italic">
                      {detailRequest.notes}
                    </p>
                  </div>
                )}

              </div>

            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-4 border-t shrink-0">
            <Button
              type="button"
              onClick={() => exportMaterialRequestPDF(detailRequest)}
              className="bg-slate-800 hover:bg-slate-900 text-white"
            >
              📄 Exportar PDF
            </Button>

            <Button
              type="button"
              onClick={closeDetailDialog}
              className="btn-secondary"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={receiptDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeReceiptDialog();
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Confirmar recepción {receiptRequest?.order_number || ''}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto pr-2 flex-1 min-h-0 space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-900">
                Verifique las cantidades realmente recibidas. Puede confirmar
                una parte ahora y volver a confirmar posteriormente lo que quede pendiente.
              </p>
            </div>

            <div className="space-y-3">
              {receiptItems.map((item) => (
                <div
                  key={item.item_index}
                  className="border rounded-lg p-4 bg-slate-50"
                >
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {item.item_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        Unidad: {item.unit}
                      </p>
                    </div>

                    <div className="text-right text-sm">
                      <p>
                        Solicitado:{' '}
                        <span className="font-semibold">
                          {item.quantity_requested}
                        </span>
                      </p>
                      <p>
                        Ya recibido:{' '}
                        <span className="font-semibold">
                          {item.quantity_delivered}
                        </span>
                      </p>
                      <p className="text-orange-700">
                        Pendiente:{' '}
                        <span className="font-semibold">
                          {item.quantity_pending}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Recibido ahora
                    </label>

                    <input
                      type="number"
                      min="0"
                      max={item.quantity_pending}
                      step="0.1"
                      value={item.quantity_received}
                      onChange={(e) => {
                        const value = e.target.value;
                        setReceiptItems((prev) =>
                          prev.map((current) =>
                            current.item_index === item.item_index
                              ? { ...current, quantity_received: value }
                              : current
                          )
                        );
                      }}
                      className="input-field"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Observaciones de recepción (Opcional)
              </label>

              <textarea
                value={receiptNotes}
                onChange={(e) => setReceiptNotes(e.target.value)}
                className="input-field"
                rows={3}
                placeholder="Indique faltantes, daños, diferencias u otra observación..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={closeReceiptDialog}
              className="btn-secondary"
            >
              Cancelar
            </Button>

            <Button
              type="button"
              onClick={handleConfirmReceipt}
              className="btn-primary"
            >
              <CheckCircle size={18} className="mr-2" />
              Confirmar recepción
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialRequests;
